import { api } from "./apiSlice";
import { unwrapApiResponse } from "../utils/apiUtils";
import type { VehicleLocation } from "./vehiclesApi";

export interface ShareTokenResponse {
  token: string;
  shareUrl: string;
  expiresAt: string;
  name: string;
}

export enum ShareAssetType {
  Vehicle = 1,
  Trailer = 2,
}

export interface CreateShareLinkRequest {
  name?: string | null;
  durationDays: number;
  vehicleIds: number[];
  trailerDeviceIds: string[];
}

export interface ShareLinkItemSummary {
  assetType: ShareAssetType;
  label: string;
}

export interface ShareLinkListItem {
  id: number;
  token: string;
  name: string | null;
  shareUrl: string;
  createdAt: string;
  expiresAt: string;
  isActive: boolean;
  isExpired: boolean;
  createdByUserName: string | null;
  items: ShareLinkItemSummary[];
}

export interface AssignedTrailerForShare {
  trailerId: number;
  unitNumber: string | null;
  plate: string | null;
  vin: string | null;
  driverName: string | null;
  deviceId: string | null;
}

export interface PublicAssetLocation {
  assetType: ShareAssetType;
  vehicleId: number | null;
  trailerId: number | null;
  deviceId: string | null;
  name: string;
  latitude: number | null;
  longitude: number | null;
  speed: number | null;
  heading: number | null;
  status: string | null;
  address: string | null;
  lastUpdate: string | null;
  licensePlateNumber: string | null;
  locationTime: string | null;
  drivingStatus: number | null;
  batteryLevel: number | null;
  supportsHistory: boolean;
}

export interface PublicTrackingResponse {
  expiresAt: string;
  assets: PublicAssetLocation[];
}

export const shareApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createShareLink: builder.mutation<
      ShareTokenResponse,
      CreateShareLinkRequest
    >({
      query: (body) => ({
        url: "share/link",
        method: "POST",
        body,
      }),
      transformResponse: (response: any) => unwrapApiResponse(response),
      invalidatesTags: ["ShareLinks"],
    }),

    getShareLinks: builder.query<ShareLinkListItem[], void>({
      query: () => "share",
      transformResponse: (response: any) => unwrapApiResponse(response),
      providesTags: ["ShareLinks"],
    }),

    getAssignedTrailer: builder.query<AssignedTrailerForShare, number>({
      query: (vehicleId) => `share/vehicle/${vehicleId}/assigned-trailer`,
      transformResponse: (response: any) => unwrapApiResponse(response),
    }),

    revokeShareLink: builder.mutation<boolean, string>({
      query: (token) => ({
        url: `share/${token}`,
        method: "DELETE",
      }),
      transformResponse: (response: any) => unwrapApiResponse(response),
      invalidatesTags: ["ShareLinks"],
    }),

    getPublicTracking: builder.query<PublicTrackingResponse, string>({
      query: (token) => `public/track/${token}`,
      transformResponse: (response: any) => unwrapApiResponse(response),
    }),

    getPublicVehicleHistory: builder.query<
      VehicleLocation[],
      { token: string; vehicleId: number; daysBack?: number }
    >({
      query: ({ token, vehicleId, daysBack = 1 }) =>
        `public/track/${token}/history?vehicleId=${vehicleId}&daysBack=${daysBack}`,
      transformResponse: (response: any) => unwrapApiResponse(response),
    }),
  }),
});

export const {
  useCreateShareLinkMutation,
  useGetShareLinksQuery,
  useGetAssignedTrailerQuery,
  useLazyGetAssignedTrailerQuery,
  useRevokeShareLinkMutation,
  useGetPublicTrackingQuery,
  useGetPublicVehicleHistoryQuery,
} = shareApi;
